import { type AnyExtension } from '@tiptap/core';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import { CharacterCount, Placeholder } from '@tiptap/extensions';
import { StarterKit as Base } from '@tiptap/starter-kit';

import { Mention } from './mention';

export interface ExtensionsProps {
  mention?: {
    items: string[];
  };
}

export function extensions({ mention }: ExtensionsProps): AnyExtension[] {
  const baseConfig: AnyExtension[] = [
    Base.configure({
      code: false,
      codeBlock: false,
      underline: false,
      bulletList: {
        keepAttributes: false,
        keepMarks: true,
      },
      dropcursor: {
        color: '#7936ec',
      },
      heading: {
        levels: [1, 2, 3, 4, 5, 6],
      },
      link: {
        autolink: false,
        defaultProtocol: 'https',
        openOnClick: false,
        protocols: [],
        isAllowedUri: (url, ctx) => {
          try {
            // construct URL
            const parsedUrl = url.includes(':')
              ? new URL(url)
              : new URL(`${ctx.defaultProtocol}://${url}`);

            // use default validation
            if (!ctx.defaultValidate(parsedUrl.href)) {
              return false;
            }

            // disallowed protocols
            const disallowedProtocols = ['ftp', 'file', 'mailto', 'http'];
            const protocol = parsedUrl.protocol.replace(':', '');

            if (disallowedProtocols.includes(protocol)) {
              return false;
            }

            // only allow HTTPS links
            const allowedProtocols = ['https'];

            if (!allowedProtocols.includes(protocol)) {
              return false;
            }

            // disallowed domains
            const disallowedDomains = ['example-phishing.com', 'malicious-site.net'];
            const domain = parsedUrl.hostname;

            if (disallowedDomains.includes(domain)) {
              return false;
            }

            // all checks have passed
            return true;
          } catch {
            return false;
          }
        },
        shouldAutoLink: (url) => {
          try {
            // construct URL
            const parsedUrl = url.includes(':') ? new URL(url) : new URL(`https://${url}`);

            // only auto-link if the domain is not in the disallowed list
            const disallowedDomains = ['example-no-autolink.com', 'another-no-autolink.com'];
            const domain = parsedUrl.hostname;

            return !disallowedDomains.includes(domain);
          } catch {
            return false;
          }
        },
      },
      orderedList: {
        keepAttributes: false,
        keepMarks: true,
      },
      undoRedo: {
        depth: 30,
      },
    }),
    Underline,
    CharacterCount,
    Image.configure({
      inline: true,
    }),
    Placeholder.configure({
      placeholder: 'Escribe',
    }),
  ];

  if (Array.isArray(mention?.items) && mention?.items.length > 0) {
    const mentionConfig: AnyExtension[] = [Mention.configure({ items: mention.items })];

    return [...baseConfig, ...mentionConfig];
  }

  return baseConfig;
}
